import React, { useState, useEffect, useCallback } from 'react';
import { schedulerAPI, flowsAPI } from '../api/client';
import apiClient from '../api/client';
import { useAuth } from '../context/AuthContext';
import {
  Clock, Bell, Plus, Play, Edit2, Trash2, ChevronDown, ChevronRight,
  RefreshCw, CheckCircle, XCircle, AlertTriangle, Info, X, Users,
  Mail, Save, Eye, History, ToggleLeft, ToggleRight, Calendar,
  Settings, Zap, Database, FileText, Send, GitBranch, ArrowUp, ArrowDown, Layers, Plug
} from 'lucide-react';

// ─── helpers ─────────────────────────────────────────────────────────────────

const CATEGORY_LABELS = {
  system:      { label: 'System',      color: 'bg-slate-100 text-slate-700' },
  finance:     { label: 'Finance',     color: 'bg-blue-100 text-blue-700' },
  compliance:  { label: 'Compliance',  color: 'bg-purple-100 text-purple-700' },
  invoice:     { label: 'Invoices',    color: 'bg-indigo-100 text-indigo-700' },
  expense:     { label: 'Expenses',    color: 'bg-orange-100 text-orange-700' },
  budget:      { label: 'Budgets',     color: 'bg-green-100 text-green-700' },
  period:      { label: 'Period End',  color: 'bg-amber-100 text-amber-700' },
  contract:    { label: 'Contracts',   color: 'bg-rose-100 text-rose-700' },
  integration: { label: 'Integration', color: 'bg-teal-100 text-teal-700' },
  custom:      { label: 'Custom',      color: 'bg-gray-100 text-gray-700' },
};

const STATUS_ICONS = {
  success: <CheckCircle className="w-4 h-4 text-green-500" />,
  failed:  <XCircle     className="w-4 h-4 text-red-500"   />,
  running: <RefreshCw   className="w-4 h-4 text-blue-500 animate-spin" />,
  skipped: <Info        className="w-4 h-4 text-gray-400"  />,
  sent:    <CheckCircle className="w-4 h-4 text-green-500" />,
};

function CronHumanLabel({ cron }) {
  const map = {
    '0 6 * * *':   'Daily at 06:00 UTC',
    '0 8 * * *':   'Daily at 08:00 UTC',
    '0 9 * * *':   'Daily at 09:00 UTC',
    '0 6 * * 1':   'Every Monday at 06:00 UTC',
    '0 8 25 * *':  '25th of each month at 08:00 UTC',
    '0 8 20 3 *':  '20 March each year at 08:00 UTC',
    '0 8 1 4 *':   '1 April each year at 08:00 UTC',
    '0 2 */2 * *': 'Every 2 days at 02:00 UTC',
  };
  return <span className="text-xs text-gray-500">{map[cron] || cron}</span>;
}

function CategoryBadge({ category }) {
  const cfg = CATEGORY_LABELS[category] || CATEGORY_LABELS.custom;
  return <span className={`inline-flex px-2 py-0.5 rounded text-[11px] font-semibold ${cfg.color}`}>{cfg.label}</span>;
}

function Toggle({ enabled, onChange }) {
  return (
    <button
      onClick={() => onChange(!enabled)}
      className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full transition-colors ${
        enabled ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
      }`}
    >
      {enabled ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
      {enabled ? 'Enabled' : 'Disabled'}
    </button>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' });
}

// ─── Log Panel ────────────────────────────────────────────────────────────────

function LogPanel({ rows, type }) {
  if (!rows.length) return <p className="text-sm text-gray-400 py-2">No history yet.</p>;
  return (
    <div className="space-y-1 max-h-56 overflow-y-auto pr-1">
      {rows.map((r, i) => (
        <div key={r.RunID || r.LogID || i} className="flex items-start gap-2 text-xs border border-gray-100 rounded px-2 py-1.5 bg-gray-50">
          <span className="flex-shrink-0 mt-0.5">{STATUS_ICONS[r.Status] || <Info className="w-4 h-4 text-gray-400" />}</span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium">{fmtDate(r.StartedAt || r.SentAt)}</span>
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                r.Status === 'success' || r.Status === 'sent' ? 'bg-green-100 text-green-700'
                : r.Status === 'failed' ? 'bg-red-100 text-red-700'
                : 'bg-gray-100 text-gray-600'
              }`}>{r.Status}</span>
            </div>
            {r.Message && <p className="text-gray-500 truncate">{r.Message}</p>}
            {r.TriggeredBy && <p className="text-gray-400">{r.TriggeredBy === 'manual' ? '⚡ Manual run' : '⏱ Scheduled'}</p>}
            {type === 'rule' && r.RecipientsCount != null && <p className="text-gray-400">{r.RecipientsCount} recipient(s)</p>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Recipients Panel ─────────────────────────────────────────────────────────

function RecipientsPanel({ ruleId, companyId, allUsers }) {
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [adding, setAdding]         = useState(false);
  const [pickMode, setPickMode]     = useState('user'); // user | external
  const [pickUserId, setPickUserId] = useState('');
  const [extEmail, setExtEmail]     = useState('');
  const [extName, setExtName]       = useState('');
  const [saving, setSaving]         = useState(false);
  const [err, setErr]               = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await schedulerAPI.getRecipients(ruleId, companyId);
      setRecipients(res.data.recipients || []);
    } catch {}
    setLoading(false);
  }, [ruleId, companyId]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    setErr('');
    setSaving(true);
    try {
      if (pickMode === 'user') {
        if (!pickUserId) { setErr('Select a user'); setSaving(false); return; }
        await schedulerAPI.addRecipient(ruleId, { userId: pickUserId, companyId });
      } else {
        if (!extEmail) { setErr('Enter an email address'); setSaving(false); return; }
        await schedulerAPI.addRecipient(ruleId, { externalEmail: extEmail, displayName: extName || extEmail, companyId });
      }
      setPickUserId(''); setExtEmail(''); setExtName('');
      setAdding(false);
      load();
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to add recipient');
    }
    setSaving(false);
  };

  const handleRemove = async (recipientId) => {
    await schedulerAPI.removeRecipient(ruleId, recipientId, companyId);
    load();
  };

  if (loading) return <p className="text-xs text-gray-400">Loading recipients…</p>;

  return (
    <div>
      <div className="space-y-1 mb-2">
        {recipients.length === 0 && <p className="text-xs text-gray-400">No specific recipients — using role-based targeting.</p>}
        {recipients.map(r => (
          <div key={r.RecipientID} className="flex items-center justify-between text-xs bg-gray-50 border border-gray-100 rounded px-2 py-1.5">
            <div className="flex items-center gap-2">
              <Mail className="w-3.5 h-3.5 text-gray-400" />
              <span>{r.FullName || r.DisplayName || r.InternalEmail || r.ExternalEmail}</span>
              <span className="text-gray-400">{r.InternalEmail || r.ExternalEmail}</span>
              {!r.UserID && <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded text-[10px] font-medium">External</span>}
            </div>
            <button onClick={() => handleRemove(r.RecipientID)} className="text-gray-400 hover:text-red-500">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>

      {adding ? (
        <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-2">
          <div className="flex gap-2">
            <button onClick={() => setPickMode('user')}    className={`px-3 py-1 text-xs rounded font-medium ${pickMode === 'user'     ? 'bg-blue-600 text-white' : 'bg-white border border-gray-300 text-gray-600'}`}>Portal User</button>
            <button onClick={() => setPickMode('external')} className={`px-3 py-1 text-xs rounded font-medium ${pickMode === 'external' ? 'bg-blue-600 text-white' : 'bg-white border border-gray-300 text-gray-600'}`}>External Email</button>
          </div>
          {pickMode === 'user' ? (
            <select value={pickUserId} onChange={e => setPickUserId(e.target.value)} className="input text-sm w-full">
              <option value="">— Select user —</option>
              {allUsers.map(u => <option key={u.UserID} value={u.UserID}>{u.FullName} ({u.Email})</option>)}
            </select>
          ) : (
            <div className="space-y-1">
              <input value={extEmail} onChange={e => setExtEmail(e.target.value)} placeholder="email@example.com" className="input text-sm w-full" />
              <input value={extName}  onChange={e => setExtName(e.target.value)}  placeholder="Display name (optional)" className="input text-sm w-full" />
            </div>
          )}
          {err && <p className="text-xs text-red-500">{err}</p>}
          <div className="flex gap-2">
            <button onClick={handleAdd} disabled={saving} className="btn btn-primary btn-sm text-xs">{saving ? 'Adding…' : 'Add'}</button>
            <button onClick={() => { setAdding(false); setErr(''); }} className="btn btn-secondary btn-sm text-xs">Cancel</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setAdding(true)} className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 font-medium mt-1">
          <Plus className="w-3.5 h-3.5" /> Add recipient
        </button>
      )}
    </div>
  );
}

// ─── Task Edit Modal ───────────────────────────────────────────────────────────

const CRON_PRESETS = [
  { label: 'Daily at 06:00 UTC',            value: '0 6 * * *'  },
  { label: 'Daily at 08:00 UTC',            value: '0 8 * * *'  },
  { label: 'Daily at 09:00 UTC',            value: '0 9 * * *'  },
  { label: 'Every Monday at 06:00 UTC',     value: '0 6 * * 1'  },
  { label: 'Every hour',                    value: '0 * * * *'  },
  { label: '1st of each month at 08:00',    value: '0 8 1 * *'  },
  { label: '25th of each month at 08:00',   value: '0 8 25 * *' },
  { label: '20 March each year at 08:00',   value: '0 8 20 3 *' },
  { label: 'Custom…',                       value: '__custom__' },
];

function TaskEditModal({ task, companyId, onClose, onSaved }) {
  const [cron, setCron]       = useState(task.CronExpression || '0 6 * * *');
  const [cronMode, setCronMode] = useState(
    CRON_PRESETS.find(p => p.value === task.CronExpression && p.value !== '__custom__') ? task.CronExpression : '__custom__'
  );
  const [customCron, setCustomCron] = useState(task.CronExpression || '');
  const [saving, setSaving]   = useState(false);
  const [err, setErr]         = useState('');

  const effectiveCron = cronMode === '__custom__' ? customCron : cronMode;

  const save = async () => {
    setErr('');
    setSaving(true);
    try {
      await schedulerAPI.updateTask(task.TaskID, { cronExpression: effectiveCron, companyId }, companyId);
      onSaved();
      onClose();
    } catch (e) {
      setErr(e.response?.data?.error || 'Save failed');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900">Edit Schedule — {task.TaskName}</h2>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Schedule</label>
            <select className="input text-sm w-full" value={cronMode} onChange={e => setCronMode(e.target.value)}>
              {CRON_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          {cronMode === '__custom__' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cron Expression</label>
              <input className="input text-sm font-mono w-full" value={customCron} onChange={e => setCustomCron(e.target.value)} placeholder="0 6 * * *" />
              <div className="mt-2 bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs space-y-2">
                <p className="font-semibold text-gray-600 uppercase tracking-wide">Format (all times UTC)</p>
                <code className="block font-mono text-gray-500">┌─ minute (0–59)</code>
                <code className="block font-mono text-gray-500">│ ┌─ hour (0–23)</code>
                <code className="block font-mono text-gray-500">│ │ ┌─ day of month (1–31)</code>
                <code className="block font-mono text-gray-500">│ │ │ ┌─ month (1–12)</code>
                <code className="block font-mono text-gray-500">│ │ │ │ ┌─ weekday (0=Sun … 6=Sat)</code>
                <code className="block font-mono text-blue-700 font-bold">* * * * *</code>
                <div className="border-t border-gray-200 pt-2 space-y-1 text-gray-500">
                  <p><code className="bg-gray-100 rounded px-1 font-mono">0 6 * * *</code> — every day at 06:00</p>
                  <p><code className="bg-gray-100 rounded px-1 font-mono">0 9 * * 1</code> — every Monday at 09:00</p>
                  <p><code className="bg-gray-100 rounded px-1 font-mono">0 8 1 * *</code> — 1st of every month at 08:00</p>
                  <p><code className="bg-gray-100 rounded px-1 font-mono">30 7 * * 1-5</code> — Mon–Fri at 07:30</p>
                  <p><code className="bg-gray-100 rounded px-1 font-mono">0 */4 * * *</code> — every 4 hours</p>
                </div>
              </div>
            </div>
          )}
          {err && <p className="text-sm text-red-500">{err}</p>}
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={save} disabled={saving} className="btn btn-primary">{saving ? 'Saving…' : 'Save'}</button>
          <button onClick={onClose} className="btn btn-secondary">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── Rule Edit / Create Modal ─────────────────────────────────────────────────

const TEMPLATE_VARS = ['{{ApproverName}}', '{{CompanyName}}', '{{Count}}', '{{PortalUrl}}', '{{InvoiceList}}'];
const ROLE_OPTIONS = ['Super Admin', 'Finance Admin', 'Finance User', 'Department Manager', 'Department Approver'];

function RuleModal({ rule, companyId, allUsers, onClose, onSaved }) {
  const isNew = !rule;
  const [form, setForm] = useState({
    ruleName:       rule?.RuleName       || '',
    description:    rule?.Description    || '',
    category:       rule?.Category       || 'invoice',
    triggerType:    rule?.TriggerType    || 'scheduled',
    cronMode:       CRON_PRESETS.find(p => p.value === rule?.CronExpression && p.value !== '__custom__')
                      ? rule.CronExpression : (rule?.CronExpression ? '__custom__' : '0 9 * * *'),
    cronCustom:     rule?.CronExpression  || '',
    thresholdDays:  rule?.ThresholdDays   != null ? String(rule.ThresholdDays) : '',
    emailSubject:   rule?.EmailSubject    || '',
    emailBody:      rule?.EmailBody       || '',
    recipientType:  rule?.RecipientType   || 'roles',
    recipientRoles: (() => { try { return JSON.parse(rule?.RecipientRoles || '[]'); } catch { return []; }})(),
  });
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState('');
  const [tab, setTab]       = useState('config'); // config | body | recipients

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const effectiveCron = form.cronMode === '__custom__' ? form.cronCustom : form.cronMode;

  const toggleRole = (role) => {
    set('recipientRoles', form.recipientRoles.includes(role)
      ? form.recipientRoles.filter(r => r !== role)
      : [...form.recipientRoles, role]
    );
  };

  const save = async () => {
    setErr('');
    if (!form.ruleName) { setErr('Rule name is required'); return; }
    if (!form.emailSubject) { setErr('Email subject is required'); return; }
    if (!form.emailBody) { setErr('Email body is required'); return; }
    setSaving(true);
    try {
      const payload = {
        companyId,
        ruleName:      form.ruleName,
        description:   form.description,
        category:      form.category,
        triggerType:   form.triggerType,
        cronExpression: form.triggerType === 'scheduled' ? effectiveCron : null,
        thresholdDays:  form.thresholdDays ? parseInt(form.thresholdDays, 10) : null,
        emailSubject:  form.emailSubject,
        emailBody:     form.emailBody,
        recipientType: form.recipientType,
        recipientRoles: form.recipientRoles,
      };
      if (isNew)  await schedulerAPI.createRule(payload);
      else        await schedulerAPI.updateRule(rule.RuleID, payload);
      onSaved();
      onClose();
    } catch (e) {
      setErr(e.response?.data?.error || 'Save failed');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[92vh]" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">{isNew ? 'New Notification Rule' : `Edit — ${rule.RuleName}`}</h2>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-6">
          {[['config','Settings'], ['body','Email Content'], ['recipients','Recipients']].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === k ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>{l}</button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {tab === 'config' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rule Name *</label>
                  <input value={form.ruleName} onChange={e => set('ruleName', e.target.value)} className="input text-sm w-full" placeholder="e.g. Invoice 7-Day Reminder" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <input value={form.description} onChange={e => set('description', e.target.value)} className="input text-sm w-full" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select value={form.category} onChange={e => set('category', e.target.value)} className="input text-sm w-full">
                    {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Trigger Type</label>
                  <select value={form.triggerType} onChange={e => set('triggerType', e.target.value)} className="input text-sm w-full">
                    <option value="scheduled">Scheduled (cron)</option>
                  </select>
                </div>
              </div>

              {form.triggerType === 'scheduled' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Schedule</label>
                    <select className="input text-sm w-full" value={form.cronMode} onChange={e => set('cronMode', e.target.value)}>
                      {CRON_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </div>
                  {form.cronMode === '__custom__' && (
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Cron Expression</label>
                      <input className="input text-sm font-mono w-full" value={form.cronCustom} onChange={e => set('cronCustom', e.target.value)} placeholder="0 9 * * *" />
                      <div className="mt-2 bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs space-y-2">
                        <p className="font-semibold text-gray-600 uppercase tracking-wide">Format (all times UTC)</p>
                        <code className="block font-mono text-gray-500">┌─ minute (0–59) │ ┌─ hour (0–23) │ │ ┌─ day (1–31) │ │ │ ┌─ month (1–12) │ │ │ │ ┌─ weekday (0=Sun…6=Sat)</code>
                        <code className="block font-mono text-blue-700 font-bold">* * * * *</code>
                        <div className="border-t border-gray-200 pt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-gray-500">
                          <p><code className="bg-gray-100 rounded px-1 font-mono">0 9 * * *</code> — daily at 09:00</p>
                          <p><code className="bg-gray-100 rounded px-1 font-mono">0 9 * * 1</code> — every Monday at 09:00</p>
                          <p><code className="bg-gray-100 rounded px-1 font-mono">0 8 1 * *</code> — 1st of month at 08:00</p>
                          <p><code className="bg-gray-100 rounded px-1 font-mono">30 7 * * 1-5</code> — Mon–Fri at 07:30</p>
                          <p><code className="bg-gray-100 rounded px-1 font-mono">0 8 25 * *</code> — 25th of month at 08:00</p>
                          <p><code className="bg-gray-100 rounded px-1 font-mono">0 */4 * * *</code> — every 4 hours</p>
                        </div>
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Threshold (days outstanding)</label>
                    <input type="number" min="0" value={form.thresholdDays} onChange={e => set('thresholdDays', e.target.value)} className="input text-sm w-full" placeholder="e.g. 1 = reminders after 1 day" />
                    <p className="text-xs text-gray-400 mt-0.5">Leave blank for period/broadcast rules</p>
                  </div>
                </div>
              )}

              {/* Recipient roles */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target Roles</label>
                <div className="flex flex-wrap gap-2">
                  {ROLE_OPTIONS.map(role => (
                    <button
                      key={role}
                      type="button"
                      onClick={() => toggleRole(role)}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                        form.recipientRoles.includes(role)
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
                      }`}
                    >
                      {role}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-1">All users with these roles will receive this notification. Individual recipients can be added in the Recipients tab.</p>
              </div>
            </>
          )}

          {tab === 'body' && (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700">
                <strong>Available template variables:</strong>{' '}
                {TEMPLATE_VARS.map(v => (
                  <code key={v} className="bg-blue-100 rounded px-1 mx-0.5 cursor-pointer hover:bg-blue-200"
                    onClick={() => set('emailBody', form.emailBody + v)}>{v}</code>
                ))}
                <span className="ml-1 text-blue-500">(click to insert into body)</span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Subject *</label>
                <input value={form.emailSubject} onChange={e => set('emailSubject', e.target.value)} className="input text-sm w-full" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Body *</label>
                <textarea
                  value={form.emailBody}
                  onChange={e => set('emailBody', e.target.value)}
                  rows={12}
                  className="input text-sm w-full font-mono text-xs resize-y"
                  placeholder="Plain text email body. Use template variables like {{ApproverName}}, {{Count}}, etc."
                />
              </div>
            </>
          )}

          {tab === 'recipients' && !isNew && (
            <div>
              <p className="text-sm text-gray-500 mb-3">
                In addition to role-based targeting, you can add specific users or external email addresses here.
              </p>
              <RecipientsPanel ruleId={rule.RuleID} companyId={companyId} allUsers={allUsers} />
            </div>
          )}
          {tab === 'recipients' && isNew && (
            <p className="text-sm text-gray-400">Save the rule first, then you can add specific recipients.</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-xl">
          <div>{err && <p className="text-sm text-red-500">{err}</p>}</div>
          <div className="flex gap-2">
            <button onClick={save} disabled={saving} className="btn btn-primary">
              <Save className="w-4 h-4" />{saving ? 'Saving…' : (isNew ? 'Create Rule' : 'Save Changes')}
            </button>
            <button onClick={onClose} className="btn btn-secondary">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Task Card ────────────────────────────────────────────────────────────────

function TaskCard({ task, companyId, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const [log, setLog]           = useState(null);
  const [loadingLog, setLoadingLog] = useState(false);
  const [running, setRunning]   = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [toggling, setToggling] = useState(false);

  const loadLog = async () => {
    if (log) return;
    setLoadingLog(true);
    try {
      const res = await schedulerAPI.getTaskLog(task.TaskID, companyId);
      setLog(res.data.log || []);
    } catch {}
    setLoadingLog(false);
  };

  const toggle = async (val) => {
    setToggling(true);
    await schedulerAPI.updateTask(task.TaskID, { isEnabled: val, companyId }, companyId);
    setToggling(false);
    onRefresh();
  };

  const runNow = async () => {
    setRunning(true);
    try {
      await schedulerAPI.runTask(task.TaskID, companyId);
      setTimeout(() => { setLog(null); if (expanded) loadLog(); onRefresh(); }, 2000);
    } catch {}
    setRunning(false);
  };

  return (
    <>
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 flex items-center gap-3">
          {/* expand */}
          <button onClick={() => { setExpanded(!expanded); if (!expanded && !log) loadLog(); }} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900 text-sm">{task.TaskName}</span>
              <CategoryBadge category={task.Category} />
              {task.LastRunStatus && (
                <span className={`flex items-center gap-1 text-xs ${
                  task.LastRunStatus === 'success' ? 'text-green-600'
                  : task.LastRunStatus === 'failed' ? 'text-red-600'
                  : 'text-gray-500'
                }`}>
                  {STATUS_ICONS[task.LastRunStatus]}
                  {task.LastRunStatus}
                </span>
              )}
            </div>
            {task.Description && <p className="text-xs text-gray-500 mt-0.5 truncate">{task.Description}</p>}
            <div className="flex items-center gap-3 mt-1">
              <CronHumanLabel cron={task.CronExpression} />
              <span className="text-xs text-gray-400">Last run: {fmtDate(task.LastRunAt)}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Toggle enabled={task.IsEnabled} onChange={toggle} />
            <button onClick={() => setEditOpen(true)} className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-blue-600" title="Edit schedule">
              <Edit2 className="w-4 h-4" />
            </button>
            <button onClick={runNow} disabled={running || !task.IsEnabled} className="flex items-center gap-1 px-2.5 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" title="Run now">
              {running ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
              Run
            </button>
          </div>
        </div>
        {expanded && (
          <div className="border-t border-gray-100 px-4 py-3 bg-gray-50">
            {task.LastRunMessage && (
              <p className={`text-xs mb-3 px-2 py-1.5 rounded ${task.LastRunStatus === 'failed' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                {task.LastRunMessage}
              </p>
            )}
            <div className="flex items-center gap-1 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              <History className="w-3.5 h-3.5" /> Run History
            </div>
            {loadingLog ? <div className="text-xs text-gray-400">Loading…</div> : <LogPanel rows={log || []} type="task" />}
          </div>
        )}
      </div>
      {editOpen && <TaskEditModal task={task} companyId={companyId} onClose={() => setEditOpen(false)} onSaved={onRefresh} />}
    </>
  );
}

// ─── Rule Card ────────────────────────────────────────────────────────────────

function RuleCard({ rule, companyId, allUsers, onRefresh }) {
  const [expanded, setExpanded]   = useState(false);
  const [log, setLog]             = useState(null);
  const [loadingLog, setLoadingLog] = useState(false);
  const [running, setRunning]     = useState(false);
  const [editOpen, setEditOpen]   = useState(false);
  const [deleting, setDeleting]   = useState(false);

  const loadLog = async () => {
    if (log) return;
    setLoadingLog(true);
    try {
      const res = await schedulerAPI.getRuleLog(rule.RuleID, companyId);
      setLog(res.data.log || []);
    } catch {}
    setLoadingLog(false);
  };

  const toggle = async (val) => {
    await schedulerAPI.updateRule(rule.RuleID, { isEnabled: val, companyId });
    onRefresh();
  };

  const runNow = async () => {
    setRunning(true);
    try {
      await schedulerAPI.runRule(rule.RuleID, companyId);
      setTimeout(() => { setLog(null); if (expanded) loadLog(); onRefresh(); }, 1500);
    } catch {}
    setRunning(false);
  };

  const del = async () => {
    if (!window.confirm(`Delete rule "${rule.RuleName}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await schedulerAPI.deleteRule(rule.RuleID, companyId);
      onRefresh();
    } catch (e) {
      alert(e.response?.data?.error || 'Delete failed');
    }
    setDeleting(false);
  };

  const roles = (() => { try { return JSON.parse(rule.RecipientRoles || '[]'); } catch { return []; }})();

  return (
    <>
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 flex items-center gap-3">
          <button onClick={() => { setExpanded(!expanded); if (!expanded && !log) loadLog(); }} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900 text-sm">{rule.RuleName}</span>
              <CategoryBadge category={rule.Category} />
              {rule.ThresholdDays != null && (
                <span className="text-xs bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded">≥{rule.ThresholdDays}d outstanding</span>
              )}
              {rule.LastTriggeredAt && <span className="text-xs text-gray-400">Last sent: {fmtDate(rule.LastTriggeredAt)}</span>}
            </div>
            {rule.Description && <p className="text-xs text-gray-500 mt-0.5 truncate">{rule.Description}</p>}
            <div className="flex items-center gap-3 mt-1 flex-wrap">
              <CronHumanLabel cron={rule.CronExpression} />
              {roles.length > 0 && (
                <span className="flex items-center gap-1 text-xs text-gray-400">
                  <Users className="w-3 h-3" /> {roles.join(', ')}
                </span>
              )}
              {rule.RecipientCount > 0 && (
                <span className="flex items-center gap-1 text-xs text-gray-400">
                  <Mail className="w-3 h-3" /> +{rule.RecipientCount} specific
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Toggle enabled={rule.IsEnabled} onChange={toggle} />
            <button onClick={() => setEditOpen(true)} className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-blue-600" title="Edit">
              <Edit2 className="w-4 h-4" />
            </button>
            {rule.RuleKey?.startsWith('custom_') && (
              <button onClick={del} disabled={deleting} className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-red-600" title="Delete">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            <button onClick={runNow} disabled={running} className="flex items-center gap-1 px-2.5 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-50" title="Test send now">
              {running ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Send
            </button>
          </div>
        </div>

        {/* Email preview strip */}
        {!expanded && (
          <div className="px-4 pb-2 flex items-start gap-1.5">
            <Mail className="w-3.5 h-3.5 text-gray-300 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-gray-400 truncate italic">{rule.EmailSubject}</p>
          </div>
        )}

        {expanded && (
          <div className="border-t border-gray-100 px-4 py-3 bg-gray-50 space-y-4">
            {/* Subject + body preview */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Email Subject</p>
              <p className="text-sm text-gray-700 font-medium">{rule.EmailSubject}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Email Body Preview</p>
              <pre className="text-xs text-gray-600 bg-white border border-gray-200 rounded p-2 max-h-32 overflow-y-auto whitespace-pre-wrap font-sans">{rule.EmailBody}</pre>
            </div>
            {/* Specific recipients */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Specific Recipients</p>
              <RecipientsPanel ruleId={rule.RuleID} companyId={companyId} allUsers={allUsers} />
            </div>
            {/* Send log */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 flex items-center gap-1"><History className="w-3.5 h-3.5" /> Send History</p>
              {loadingLog ? <p className="text-xs text-gray-400">Loading…</p> : <LogPanel rows={log || []} type="rule" />}
            </div>
          </div>
        )}
      </div>
      {editOpen && <RuleModal rule={rule} companyId={companyId} allUsers={allUsers} onClose={() => setEditOpen(false)} onSaved={() => { setLog(null); onRefresh(); }} />}
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function SchedulerPage() {
  const { user, currentCompanyId } = useAuth();
  const companyId = currentCompanyId || user?.currentCompanyId;

  const [tasks, setTasks]     = useState([]);
  const [rules, setRules]     = useState([]);
  const [flows, setFlows]     = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [tab, setTab]         = useState('tasks'); // tasks | rules | flows | integrations
  const [newRuleOpen, setNewRuleOpen] = useState(false);
  const [flowModalOpen, setFlowModalOpen] = useState(false);
  const [editingFlow, setEditingFlow]     = useState(null);
  const [migrating, setMigrating]    = useState(false);
  const [migrateMsg, setMigrateMsg]  = useState('');
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [webhookLoading, setWebhookLoading] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      const [taskRes, ruleRes, userRes, flowRes] = await Promise.all([
        schedulerAPI.getTasks(companyId),
        schedulerAPI.getRules(companyId),
        schedulerAPI.getUsers(companyId),
        flowsAPI.getFlows(companyId),
      ]);
      setTasks(taskRes.data.tasks || []);
      setRules(ruleRes.data.rules || []);
      setAllUsers(userRes.data.users || []);
      setFlows(flowRes.data.flows || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load scheduler data');
    }
    setLoading(false);
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const runMigration = async () => {
    setMigrating(true);
    setMigrateMsg('');
    try {
      await schedulerAPI.migrate();
      setMigrateMsg('Migration complete. Refreshing…');
      setTimeout(load, 1000);
    } catch (e) {
      setMigrateMsg(e.response?.data?.error || 'Migration failed');
    }
    setMigrating(false);
  };

  // Group tasks/rules by category
  const taskGroups = tasks.reduce((acc, t) => { (acc[t.Category] = acc[t.Category] || []).push(t); return acc; }, {});
  const ruleGroups = rules.reduce((acc, r) => { (acc[r.Category] = acc[r.Category] || []).push(r); return acc; }, {});

  const enabledTasks = tasks.filter(t => t.IsEnabled).length;
  const enabledRules = rules.filter(r => r.IsEnabled).length;
  const enabledFlows = flows.filter(f => f.IsEnabled).length;

  const integrationTasks = tasks.filter(t => t.Category === 'integration');
  const activeIntegrationTasks = integrationTasks.filter(t => t.IsEnabled);

  const loadWebhookEvents = useCallback(async () => {
    if (!companyId) return;
    setWebhookLoading(true);
    try {
      const res = await apiClient.get('/webhooks/events');
      setWebhookEvents((res?.data?.events || []).slice(0, 10));
    } catch { setWebhookEvents([]); }
    setWebhookLoading(false);
  }, [companyId]);

  useEffect(() => {
    if (tab === 'integrations') loadWebhookEvents();
  }, [tab, loadWebhookEvents]);

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Clock className="w-6 h-6 text-blue-600" /> Scheduler & Notifications
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage automated tasks, email reminder rules and notification schedules</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="btn btn-secondary btn-sm" title="Refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          {user?.isSuperAdmin && (
            <button onClick={runMigration} disabled={migrating} className="btn btn-secondary btn-sm text-xs">
              <Database className="w-4 h-4" />{migrating ? 'Migrating…' : 'Run DB Migration'}
            </button>
          )}
        </div>
      </div>

      {migrateMsg && (
        <div className={`mb-4 px-4 py-2 rounded-lg text-sm ${migrateMsg.includes('omplete') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
          {migrateMsg}
        </div>
      )}

      {/* KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[
          { label: 'Scheduled Tasks',     value: tasks.length,       icon: Zap,      sub: `${enabledTasks} active`,      color: 'border-blue-500' },
          { label: 'Notification Rules',  value: rules.length,       icon: Bell,     sub: `${enabledRules} active`,      color: 'border-amber-500' },
          { label: 'Rules — Invoices',    value: rules.filter(r => r.Category === 'invoice').length, icon: FileText, sub: 'approval reminders', color: 'border-indigo-500' },
          { label: 'Period End Rules',    value: rules.filter(r => r.Category === 'period').length,  icon: Calendar, sub: 'month/year end',     color: 'border-green-500' },
          { label: 'Automation Flows',     value: flows.length,       icon: GitBranch, sub: `${enabledFlows} active`,      color: 'border-violet-500' },
        ].map((card, i) => {
          const Icon = card.icon;
          return (
            <div key={i} className={`bg-white rounded-lg shadow px-4 py-3 border-l-4 ${card.color}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{card.label}</p>
                  <p className="text-2xl font-bold text-gray-900">{card.value}</p>
                  <p className="text-xs text-gray-400">{card.sub}</p>
                </div>
                <Icon className="w-7 h-7 text-gray-300" />
              </div>
            </div>
          );
        })}
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2 text-sm">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />{error}
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex border border-gray-200 rounded-lg overflow-hidden">
          <button onClick={() => setTab('tasks')} className={`px-4 py-2 text-sm font-medium flex items-center gap-2 ${tab === 'tasks' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
            <Zap className="w-4 h-4" /> System Tasks
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${tab === 'tasks' ? 'bg-blue-500' : 'bg-gray-100 text-gray-500'}`}>{tasks.length}</span>
          </button>
          <button onClick={() => setTab('rules')} className={`px-4 py-2 text-sm font-medium flex items-center gap-2 ${tab === 'rules' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
            <Bell className="w-4 h-4" /> Notification Rules
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${tab === 'rules' ? 'bg-blue-500' : 'bg-gray-100 text-gray-500'}`}>{rules.length}</span>
          </button>
          <button onClick={() => setTab('flows')} className={`px-4 py-2 text-sm font-medium flex items-center gap-2 ${tab === 'flows' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
            <GitBranch className="w-4 h-4" /> Automation Flows
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${tab === 'flows' ? 'bg-blue-500' : 'bg-gray-100 text-gray-500'}`}>{flows.length}</span>
          </button>
          <button onClick={() => setTab('integrations')} className={`px-4 py-2 text-sm font-medium flex items-center gap-2 ${tab === 'integrations' ? 'bg-teal-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
            <Plug className="w-4 h-4" /> Integrations
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${tab === 'integrations' ? 'bg-teal-500' : 'bg-gray-100 text-gray-500'}`}>{activeIntegrationTasks.length}</span>
          </button>
        </div>
        {tab === 'rules' && (
          <button onClick={() => setNewRuleOpen(true)} className="btn btn-primary btn-sm">
            <Plus className="w-4 h-4" /> New Rule
          </button>
        )}
        {tab === 'flows' && (
          <button onClick={() => { setEditingFlow(null); setFlowModalOpen(true); }} className="btn btn-primary btn-sm">
            <Plus className="w-4 h-4" /> New Flow
          </button>
        )}
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16">
          <RefreshCw className="w-7 h-7 text-blue-500 animate-spin" />
        </div>
      )}

      {/* Tasks Panel */}
      {!loading && tab === 'tasks' && (
        <div className="space-y-6">
          {Object.keys(taskGroups).length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <Database className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No tasks found. Run the DB migration to seed default tasks.</p>
            </div>
          )}
          {Object.entries(taskGroups).map(([cat, items]) => (
            <div key={cat}>
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2 flex items-center gap-2">
                <CategoryBadge category={cat} />{CATEGORY_LABELS[cat]?.label || cat} Tasks
              </h3>
              <div className="space-y-2">
                {items.map(task => <TaskCard key={task.TaskID} task={task} companyId={companyId} onRefresh={load} />)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Rules Panel */}
      {!loading && tab === 'rules' && (
        <div className="space-y-6">
          {Object.keys(ruleGroups).length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <Bell className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No notification rules found. Run the DB migration to seed defaults, or create a new rule.</p>
            </div>
          )}
          {Object.entries(ruleGroups).map(([cat, items]) => (
            <div key={cat}>
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2 flex items-center gap-2">
                <CategoryBadge category={cat} />{CATEGORY_LABELS[cat]?.label || cat}
              </h3>
              <div className="space-y-2">
                {items.map(rule => <RuleCard key={rule.RuleID} rule={rule} companyId={companyId} allUsers={allUsers} onRefresh={load} />)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Flows Panel */}
      {!loading && tab === 'flows' && (
        <div className="space-y-3">
          {flows.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <GitBranch className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No automation flows yet. Create one to chain tasks together.</p>
            </div>
          )}
          {flows.map(flow => (
            <FlowCard
              key={flow.FlowID}
              flow={flow}
              companyId={companyId}
              tasks={tasks}
              rules={rules}
              onRefresh={load}
              onEdit={f => { setEditingFlow(f); setFlowModalOpen(true); }}
            />
          ))}
        </div>
      )}

      {/* Template variable info box */}
      {!loading && (
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-700">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold mb-1">Email Template Variables</p>
              <p className="text-xs">Use these in email subjects and bodies: <code className="bg-blue-100 rounded px-1">{'{{ApproverName}}'}</code> <code className="bg-blue-100 rounded px-1">{'{{CompanyName}}'}</code> <code className="bg-blue-100 rounded px-1">{'{{Count}}'}</code> <code className="bg-blue-100 rounded px-1">{'{{PortalUrl}}'}</code> <code className="bg-blue-100 rounded px-1">{'{{InvoiceList}}'}</code></p>
            </div>
          </div>
        </div>
      )}

      {/* Integrations Panel */}
      {!loading && tab === 'integrations' && (
        <div className="space-y-6">
          {/* Active ERP tasks */}
          {activeIntegrationTasks.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-center text-gray-400 py-4">
                <Plug className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm font-medium text-gray-600 mb-1">No ERP integration tasks are enabled</p>
                <p className="text-xs text-gray-400 mb-4">Enable tasks below to activate automatic subscription renewal and payment status sync.</p>
              </div>
              {integrationTasks.length > 0 && (
                <div className="border border-dashed border-teal-200 rounded-lg divide-y divide-gray-100">
                  {integrationTasks.map(task => (
                    <div key={task.TaskID} className="flex items-center justify-between px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-gray-700">{task.TaskName}</p>
                        <p className="text-xs text-gray-400">{task.Description}</p>
                      </div>
                      <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">Disabled</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow divide-y divide-gray-100">
              <div className="px-4 py-3 bg-teal-50 rounded-t-lg">
                <h3 className="text-sm font-semibold text-teal-800 flex items-center gap-2">
                  <Plug className="w-4 h-4" /> Active ERP Tasks
                </h3>
              </div>
              {activeIntegrationTasks.map(task => (
                <div key={task.TaskID} className="flex items-center justify-between px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{task.TaskName}</p>
                    <p className="text-xs text-gray-500"><CronHumanLabel cron={task.CronExpression} /></p>
                  </div>
                  <span className="inline-flex items-center gap-1 text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full font-medium">
                    <CheckCircle className="w-3 h-3" /> Active
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Webhook Events Log */}
          <div className="bg-white rounded-lg shadow">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                <History className="w-4 h-4 text-teal-600" /> Recent Webhook Events
              </h3>
              <button onClick={loadWebhookEvents} disabled={webhookLoading} className="text-xs text-teal-600 hover:underline flex items-center gap-1">
                <RefreshCw className={`w-3 h-3 ${webhookLoading ? 'animate-spin' : ''}`} /> Refresh
              </button>
            </div>
            {webhookLoading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-5 h-5 text-teal-500 animate-spin" />
              </div>
            ) : webhookEvents.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <p className="text-sm">No webhook events received yet.</p>
                <p className="text-xs mt-1">Events appear here when BC or Xero sends notifications.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-gray-500">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Source</th>
                      <th className="px-3 py-2 text-left font-medium">Type</th>
                      <th className="px-3 py-2 text-left font-medium">Resource ID</th>
                      <th className="px-3 py-2 text-left font-medium">Status</th>
                      <th className="px-3 py-2 text-left font-medium">Received</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {webhookEvents.map(ev => (
                      <tr key={ev.EventID} className="hover:bg-gray-50">
                        <td className="px-3 py-2">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${ev.Source === 'xero' ? 'bg-blue-100 text-blue-700' : 'bg-indigo-100 text-indigo-700'}`}>
                            {ev.Source === 'xero' ? 'Xero' : 'BC'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-700">{ev.EventType}</td>
                        <td className="px-3 py-2 text-gray-400 font-mono truncate max-w-[120px]">{ev.ResourceID || '—'}</td>
                        <td className="px-3 py-2">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                            ev.Status === 'processed' ? 'bg-green-100 text-green-700' :
                            ev.Status === 'failed'    ? 'bg-red-100 text-red-700' :
                            ev.Status === 'pending'   ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-500'
                          }`}>{ev.Status}</span>
                        </td>
                        <td className="px-3 py-2 text-gray-400">
                          {ev.ReceivedAt ? new Date(ev.ReceivedAt).toLocaleString() : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* New rule modal */}
      {newRuleOpen && (
        <RuleModal companyId={companyId} allUsers={allUsers} onClose={() => setNewRuleOpen(false)} onSaved={load} />
      )}

      {/* Flow modal */}
      {flowModalOpen && (
        <FlowModal
          companyId={companyId}
          flow={editingFlow}
          onClose={() => { setFlowModalOpen(false); setEditingFlow(null); }}
          onSaved={load}
        />
      )}
    </div>
  );
}

// ─── FlowCard ─────────────────────────────────────────────────────────────────
function FlowCard({ flow, companyId, tasks, rules, onRefresh, onEdit }) {
  const [expanded, setExpanded] = useState(false);
  const [steps, setSteps]       = useState([]);
  const [log, setLog]           = useState([]);
  const [loadingSteps, setLoadingSteps] = useState(false);
  const [showLog, setShowLog]   = useState(false);
  const [running, setRunning]   = useState(false);

  const loadSteps = useCallback(async () => {
    if (!flow.FlowID) return;
    setLoadingSteps(true);
    try {
      const res = await flowsAPI.getSteps(companyId, flow.FlowID);
      setSteps(res.data.steps || []);
    } catch {}
    setLoadingSteps(false);
  }, [flow.FlowID, companyId]);

  useEffect(() => { if (expanded) loadSteps(); }, [expanded, loadSteps]);

  const loadLog = async () => {
    try { const res = await flowsAPI.getFlowLog(companyId, flow.FlowID); setLog(res.data.log || []); } catch {}
    setShowLog(true);
  };

  const toggleEnabled = async () => {
    try { await flowsAPI.updateFlow(companyId, flow.FlowID, { IsEnabled: !flow.IsEnabled }); onRefresh(); } catch {}
  };

  const deleteFlow = async () => {
    if (!window.confirm(`Delete flow "${flow.FlowName}"?`)) return;
    try { await flowsAPI.deleteFlow(companyId, flow.FlowID); onRefresh(); } catch {}
  };

  const runNow = async () => {
    setRunning(true);
    try { await flowsAPI.runFlow(companyId, flow.FlowID); onRefresh(); } catch {}
    setRunning(false);
  };

  const moveStep = async (stepId, direction) => {
    const idx = steps.findIndex(s => s.StepID === stepId);
    const newOrder = [...steps];
    const swap = idx + direction;
    if (swap < 0 || swap >= newOrder.length) return;
    [newOrder[idx], newOrder[swap]] = [newOrder[swap], newOrder[idx]];
    try {
      await flowsAPI.reorderSteps(companyId, flow.FlowID, newOrder.map(s => s.StepID));
      loadSteps();
    } catch {}
  };

  const removeStep = async (stepId) => {
    try { await flowsAPI.deleteStep(companyId, flow.FlowID, stepId); loadSteps(); } catch {}
  };

  const addStep = async (type, refId, label) => {
    try { await flowsAPI.addStep(companyId, flow.FlowID, { StepType: type, RefID: refId, StepLabel: label }); loadSteps(); } catch {}
  };

  return (
    <div className="bg-white rounded-lg shadow border border-gray-100">
      <div className="flex items-center justify-between px-4 py-3 cursor-pointer" onClick={() => setExpanded(e => !e)}>
        <div className="flex items-center gap-3">
          {expanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
          <GitBranch className="w-4 h-4 text-violet-500" />
          <div>
            <p className="font-medium text-gray-900 text-sm">{flow.FlowName}</p>
            <p className="text-xs text-gray-400">{flow.CronExpression} {flow.Description ? '· ' + flow.Description : ''}</p>
          </div>
        </div>
        <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
          {flow.LastRunStatus && (
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              flow.LastRunStatus === 'success' ? 'bg-green-100 text-green-700' :
              flow.LastRunStatus === 'failed'  ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-500'
            }`}>{flow.LastRunStatus}</span>
          )}
          <button onClick={toggleEnabled} title="Toggle enabled">
            {flow.IsEnabled ? <ToggleRight className="w-5 h-5 text-green-500" /> : <ToggleLeft className="w-5 h-5 text-gray-400" />}
          </button>
          <button onClick={runNow} disabled={running} className="btn btn-secondary btn-xs" title="Run now">
            <Play className={`w-3.5 h-3.5 ${running ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={() => onEdit(flow)} className="btn btn-secondary btn-xs" title="Edit">
            <Edit2 className="w-3.5 h-3.5" />
          </button>
          <button onClick={deleteFlow} className="btn btn-danger btn-xs" title="Delete">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-100 px-4 py-3">
          {loadingSteps ? (
            <p className="text-xs text-gray-400">Loading steps…</p>
          ) : (
            <>
              <div className="space-y-1 mb-3">
                {steps.length === 0 && <p className="text-xs text-gray-400 italic">No steps added yet.</p>}
                {steps.map((step, idx) => (
                  <div key={step.StepID} className="flex items-center gap-2 bg-gray-50 rounded px-3 py-1.5 text-sm">
                    <span className="text-gray-400 text-xs w-5 text-right">{step.StepOrder}.</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                      step.StepType === 'task' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'
                    }`}>{step.StepType}</span>
                    <span className="flex-1 text-gray-700">{step.StepLabel || step.RefKey}</span>
                    <div className="flex gap-1">
                      <button onClick={() => moveStep(step.StepID, -1)} disabled={idx === 0} className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30">
                        <ArrowUp className="w-3 h-3" />
                      </button>
                      <button onClick={() => moveStep(step.StepID, 1)} disabled={idx === steps.length - 1} className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30">
                        <ArrowDown className="w-3 h-3" />
                      </button>
                      <button onClick={() => removeStep(step.StepID)} className="p-0.5 text-red-400 hover:text-red-600">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <StepAdder tasks={tasks} rules={rules} onAdd={addStep} />
              <button onClick={loadLog} className="mt-2 text-xs text-blue-600 hover:underline flex items-center gap-1">
                <History className="w-3.5 h-3.5" /> View run log
              </button>
              {showLog && (
                <div className="mt-2 border rounded bg-gray-50 text-xs divide-y max-h-48 overflow-y-auto">
                  {log.length === 0 && <p className="p-2 text-gray-400">No runs yet.</p>}
                  {log.map(entry => (
                    <div key={entry.RunID} className="flex items-center gap-3 px-3 py-1.5">
                      <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                        entry.Status === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                      }`}>{entry.Status}</span>
                      <span className="text-gray-500">{new Date(entry.StartedAt).toLocaleString()}</span>
                      {entry.ErrorMessage && <span className="text-red-500 truncate">{entry.ErrorMessage}</span>}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── StepAdder ────────────────────────────────────────────────────────────────
function StepAdder({ tasks, rules, onAdd }) {
  const [open, setOpen]     = useState(false);
  const [type, setType]     = useState('task');
  const [refId, setRefId]   = useState('');

  const options = type === 'task' ? tasks : rules;
  const idKey   = type === 'task' ? 'TaskID' : 'RuleID';
  const nameKey = type === 'task' ? 'TaskName' : 'RuleName';

  const handleAdd = () => {
    if (!refId) return;
    const selected = options.find(o => String(o[idKey]) === String(refId));
    onAdd(type, refId, selected?.[nameKey] || refId);
    setRefId('');
    setOpen(false);
  };

  if (!open) return (
    <button onClick={() => setOpen(true)} className="text-xs text-violet-600 hover:underline flex items-center gap-1">
      <Plus className="w-3 h-3" /> Add step
    </button>
  );

  return (
    <div className="flex items-center gap-2 flex-wrap mt-1">
      <select value={type} onChange={e => { setType(e.target.value); setRefId(''); }} className="input input-sm text-xs py-1">
        <option value="task">Task</option>
        <option value="rule">Rule</option>
      </select>
      <select value={refId} onChange={e => setRefId(e.target.value)} className="input input-sm text-xs py-1 flex-1">
        <option value="">— select —</option>
        {options.map(o => (
          <option key={o[idKey]} value={o[idKey]}>{o[nameKey]}</option>
        ))}
      </select>
      <button onClick={handleAdd} disabled={!refId} className="btn btn-primary btn-xs">Add</button>
      <button onClick={() => setOpen(false)} className="btn btn-secondary btn-xs">Cancel</button>
    </div>
  );
}

// ─── FlowModal ────────────────────────────────────────────────────────────────
function FlowModal({ companyId, flow, onClose, onSaved }) {
  const [form, setForm] = useState({
    FlowName: flow?.FlowName || '',
    Description: flow?.Description || '',
    CronExpression: flow?.CronExpression || '0 8 * * *',
    IsEnabled: flow?.IsEnabled ?? true,
    ContinueOnError: flow?.ContinueOnError ?? false,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState('');

  const save = async () => {
    if (!form.FlowName.trim()) { setErr('Flow name is required'); return; }
    setSaving(true);
    setErr('');
    try {
      if (flow?.FlowID) {
        await flowsAPI.updateFlow(companyId, flow.FlowID, form);
      } else {
        await flowsAPI.createFlow(companyId, form);
      }
      onSaved();
      onClose();
    } catch (e) {
      setErr(e.response?.data?.error || 'Save failed');
    }
    setSaving(false);
  };

  const CRON_PRESETS = [
    { label: 'Every hour',        value: '0 * * * *' },
    { label: 'Daily at 08:00',    value: '0 8 * * *' },
    { label: 'Daily at midnight', value: '0 0 * * *' },
    { label: 'Weekly Monday',     value: '0 8 * * 1' },
    { label: 'Monthly 1st',       value: '0 8 1 * *' },
    { label: 'Quarterly (Jan/Apr/Jul/Oct)', value: '0 8 1 1,4,7,10 *' },
  ];

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <GitBranch className="w-5 h-5 text-violet-500" />
            {flow ? 'Edit Flow' : 'New Automation Flow'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        {err && <p className="text-red-600 text-sm mb-3">{err}</p>}

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Flow Name *</label>
            <input value={form.FlowName} onChange={e => setForm(f => ({ ...f, FlowName: e.target.value }))} className="input w-full" placeholder="e.g. Month-End Close" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
            <input value={form.Description} onChange={e => setForm(f => ({ ...f, Description: e.target.value }))} className="input w-full" placeholder="Optional description" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Schedule (Cron Expression)</label>
            <input
              value={form.CronExpression}
              onChange={e => setForm(f => ({ ...f, CronExpression: e.target.value }))}
              className="input w-full font-mono text-sm"
              placeholder="0 8 * * *"
            />
            <p className="text-xs text-gray-400 mt-0.5">Format: minute hour day month weekday &nbsp;|&nbsp;
              <select
                onChange={e => { if (e.target.value) { setForm(f => ({ ...f, CronExpression: e.target.value })); e.target.value = ''; } }}
                className="text-xs text-blue-600 bg-transparent border-none cursor-pointer outline-none"
                defaultValue=""
              >
                <option value="">quick presets…</option>
                {CRON_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </p>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="checkbox" checked={form.IsEnabled} onChange={e => setForm(f => ({ ...f, IsEnabled: e.target.checked }))} />
              Enabled
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="checkbox" checked={form.ContinueOnError} onChange={e => setForm(f => ({ ...f, ContinueOnError: e.target.checked }))} />
              Continue on step error
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="btn btn-secondary">Cancel</button>
          <button onClick={save} disabled={saving} className="btn btn-primary">
            <Save className="w-4 h-4" />{saving ? 'Saving…' : 'Save Flow'}
          </button>
        </div>
      </div>
    </div>
  );
}
